// @spec IAM-STR-031
'use client';

import { useUser } from '@clerk/clerk-react';
import * as React from 'react';

import { useIsImpersonating, useValidateImpersonation } from '../api/use-impersonation';
import { useImpersonationStore } from '../stores/impersonation-store';

import { ImpersonationBanner } from './impersonation-banner';

export interface ImpersonationProviderProps {
  children: React.ReactNode;
}

/**
 * Provider component for impersonation functionality.
 *
 * Wraps the application and:
 * - Validates any existing impersonation token on mount
 * - Clears invalid/expired tokens
 * - Renders the ImpersonationBanner when active
 * - Adds top padding to content when banner is visible
 *
 * @example
 * // In providers.tsx or root layout
 * <ImpersonationProvider>
 *   <App />
 * </ImpersonationProvider>
 */
export function ImpersonationProvider({ children }: ImpersonationProviderProps) {
  const { isSignedIn, user } = useUser();
  const token = useImpersonationStore.use.token();
  const clearImpersonation = useImpersonationStore.use.clearImpersonation();
  const isImpersonating = useIsImpersonating();

  // Validate token on mount if present (hook handles clearing invalid tokens)
  useValidateImpersonation();

  // Check if user is a Super Admin (only Super Admins can impersonate)
  const isSuperAdmin = React.useMemo(() => {
    if (!user?.publicMetadata) return false;
    return user.publicMetadata.role === 'super_admin';
  }, [user]);

  // Clear impersonation if user is not a Super Admin
  React.useEffect(() => {
    if (isSignedIn && !isSuperAdmin && token) {
      clearImpersonation();
    }
  }, [isSignedIn, isSuperAdmin, token, clearImpersonation]);

  // Clear impersonation if user signs out
  React.useEffect(() => {
    if (!isSignedIn && token) {
      clearImpersonation();
    }
  }, [isSignedIn, token, clearImpersonation]);

  return (
    <>
      {/* Banner is positioned fixed, so it renders outside the flow */}
      <ImpersonationBanner />

      {/* Add padding when banner is visible to prevent content overlap */}
      <div className={isImpersonating ? 'pt-12' : ''}>{children}</div>
    </>
  );
}
